import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Mail, ArrowRight, CheckCircle, Loader2 } from 'lucide-react'
import { z } from 'zod'
import { Button } from '@/shared/components/ui/button'
import { Logo } from '@/shared/components/ui/logo'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import api from '@/consumer/lib/api'
import { setAuthToken } from '@/shared/lib/auth-token'
import { routeAfterAuth } from '@/consumer/lib/post-auth'
import { useConsumerSession } from '@/consumer/hooks/use-consumer-session'
import { rememberTenantSlug, resolveTenantSlug } from '@/consumer/lib/tenant-slug'
import { buildApiUrl } from '@/shared/lib/api-origin'

import { toastApiError } from '@/shared/lib/api-error'
interface ConsumerBranding {
  branding_mode: 'GENERIC' | 'COBRANDED' | 'BRANDED' | 'DISABLED'
  org_name: string | null
  org_logo_url: string | null
  primary_colour: string
}

const DEV_ACCOUNTS = [
  { label: 'Consumer', email: 'developers+demo-consumer@iwarranty.co' },
]

type LoginValues = { email: string }

export default function LoginPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const nextPath = searchParams.get('next') || ''
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [branding, setBranding] = useState<ConsumerBranding | null>(null)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const { data: session, isLoading: sessionLoading } = useConsumerSession()
  // A real session always carries a uuid; guard on it rather than a truthy body
  // so a non-session payload (e.g. an empty list) is never read as authenticated.
  const isAuthenticated = Boolean(session?.uuid)

  // Already signed in - route back to where they were headed (or the wallet)
  // instead of showing the magic-link form. `submitted` guards the "check your
  // email" confirmation so it isn't redirected away.
  useEffect(() => {
    if (sessionLoading || submitted || !isAuthenticated) return
    routeAfterAuth(
      { next_path: nextPath || undefined, profile: { needs_onboarding: session?.needs_onboarding } },
      navigate,
    )
  }, [sessionLoading, isAuthenticated, session, submitted, nextPath, navigate])
  const fields = useMemo<FormField[]>(() => [
    {
      type: 'email',
      name: 'email',
      placeholder: 'you@example.com',
      value: '',
      autoFocus: true,
      leadingIcon: <Mail className="h-4 w-4" />,
      validationSchema: z.string().min(1, 'Enter a valid email').email('Enter a valid email'),
    },
  ], [])
  const { Form } = useForm(
    {
      name: 'consumer-login-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as LoginValues
      setLoading(true)
      try {
        await api.post('/auth/request-access/', { email: values.email.trim(), next_path: nextPath })
        setSubmittedEmail(values.email.trim())
        setSubmitted(true)
      } catch (err) {
        toastApiError(err, 'Something went wrong. Please try again.');
      } finally {
        setLoading(false)
      }
    },
    [nextPath],
  )

  const tenantSlugFromUrl = searchParams.get('tenant') || ''
  useEffect(() => {
    let cancelled = false
    // Effective slug: tenant subdomain (authoritative) > URL param > build-time
    // env > last-used stored fallback. On `<slug>.domain` the host pins the
    // brand; the stored fallback only kicks in on a generic host, keeping a
    // customer on their tenant's brand instead of the backend's first-active
    // default (which surfaced the wrong brand for Teknor Apex customers).
    const slug = resolveTenantSlug(tenantSlugFromUrl)
    const params = new URLSearchParams()
    if (slug) {
      params.set('tenant', slug)
    } else if (nextPath) {
      params.set('next_path', nextPath)
    }
    const qs = params.toString() ? `?${params.toString()}` : ''
    fetch(buildApiUrl(`/api/public/consumer-branding/${qs}`))
      .then((res) => res.json())
      .then((json) => {
        if (!cancelled && json.result) setBranding(json.result)
        // Persist an explicit URL slug so later slug-less visits stay on-brand.
        if (tenantSlugFromUrl) rememberTenantSlug(tenantSlugFromUrl)
      })
      .catch(() => {
        // Ignore - fall back to generic branding
      })
    return () => { cancelled = true }
  }, [nextPath, tenantSlugFromUrl])

  const mode = branding?.branding_mode ?? 'GENERIC'
  const isBranded = mode === 'BRANDED' || mode === 'COBRANDED'

  async function devQuickLogin(devEmail: string) {
    try {
      const res = await api.post('/auth/dev-login/', {
        email: devEmail,
        next_path: nextPath,
      })
      if (res.data?.session_token) {
        setAuthToken(res.data.session_token)
      }
      await queryClient.invalidateQueries({ queryKey: ['consumer-session'] })
      routeAfterAuth(res.data, navigate)
    } catch (err) {
      toastApiError(err, 'Dev login failed - is the backend running?');
    }
  }

  /** Render the org logo (for COBRANDED / BRANDED modes) */
  const renderOrgLogo = (maxH = 'h-12', maxW = 'max-w-[240px]') => {
    if (!branding?.org_logo_url) {
      return (
        <span className="font-display text-[18px] text-[var(--ink)]">
          {branding?.org_name}
        </span>
      )
    }
    return (
      <img
        src={branding.org_logo_url}
        alt={branding.org_name ?? ''}
        className={`${maxH} ${maxW} object-contain`}
      />
    )
  }

  // Hold the form back until the session probe settles (unless the magic-link
  // confirmation is showing), so an authenticated visitor is redirected rather
  // than flashing the login form.
  if (!submitted && (sessionLoading || isAuthenticated)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {/* Left panel - branding */}
      <div className="hidden flex-col justify-between bg-[var(--brand)] p-12 lg:flex lg:w-[45%]">
        {/* Top: iWarranty logo (GENERIC/COBRANDED) or org logo (BRANDED) */}
        <div>
          {mode === 'BRANDED' && branding?.org_logo_url ? (
            <img
              src={branding.org_logo_url}
              alt={branding.org_name ?? ''}
              className="h-8 max-w-[200px] object-contain"
            />
          ) : (
            <Logo size="lg" variant="white" />
          )}
        </div>

        <div>
          <h1 className="mb-4 font-display text-[44px] leading-[1.05] text-white">
            Your warranties,
            <br />
            always accessible.
          </h1>
          <p className="text-white/70 text-[13px] leading-relaxed max-w-md">
            Register your products, track warranty coverage,
            and submit claims - all in one place.
          </p>
        </div>

        <div className="flex items-center gap-6 text-[10px] uppercase tracking-[0.12em] text-white/40">
          <span>Secure</span>
          <span>·</span>
          <span>Instant Access</span>
          <span>·</span>
          <span>No Password Required</span>
        </div>
      </div>
      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          {/* Mobile logo - hidden on confirmation screen to avoid duplicate */}
          <div className={`mb-10 lg:hidden ${submitted ? 'hidden' : ''}`}>
            {isBranded ? (
              <div className="flex flex-col gap-3">
                {renderOrgLogo('h-7', 'max-w-[160px]')}
                {mode === 'COBRANDED' && (
                  <div className="flex items-center gap-2 text-[10px] text-[var(--ink-3)]">
                    <span>Powered by</span>
                    <Logo size="sm" />
                  </div>
                )}
              </div>
            ) : (
              <Logo size="lg" />
            )}
          </div>

          {!submitted ? (
            <>
              {/* Org logo above form (COBRANDED/BRANDED only) */}
              {isBranded && (
                <div className="hidden lg:block mb-6">
                  {renderOrgLogo()}
                </div>
              )}

              <h2 className="mb-1 font-display text-[32px] text-[var(--ink)]">
                Enter your email to continue
              </h2>
              <p className="mb-8 text-[12px] text-[var(--ink-3)]">
                We'll send a secure link to your inbox - no account or password needed
              </p>

              <Form className="mb-5" />
              <Button
                type="submit"
                form="consumer-login-form"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-[4px] bg-[var(--green)] px-4 py-2.5 text-[12px] tracking-[0.06em] text-white transition-colors hover:bg-[var(--green-hover)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {loading ? 'Sending…' : (
                  <>
                    Send magic link
                    <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </Button>

              <p className="mt-6 text-center text-[11px] text-[var(--ink-3)]">
                We'll email you a secure link - no password needed.
              </p>

              {/* Powered by iWarranty - COBRANDED desktop */}
              {mode === 'COBRANDED' && (
                <div className="mt-8 hidden items-center justify-center gap-2 text-[10px] text-[var(--ink-3)] lg:flex">
                  <span>Powered by</span>
                  <Logo size="sm" />
                </div>
              )}

              {/* Dev quick-links */}
              {import.meta.env.DEV && (
                <div className="mt-10 border-t-[0.5px] border-t-[var(--rule)] pt-6">
                  <p className="clara-label mb-3">Dev Accounts</p>
                  <div className="flex gap-2">
                    {DEV_ACCOUNTS.map((a) => (
                      <Button
                        key={a.email}
                        type="button"
                        onClick={() => devQuickLogin(a.email)}
                        variant="outline"
                        className="rounded-full border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all hover:border-[var(--green)] hover:bg-[var(--green)] hover:text-[var(--bg)]"
                      >
                        {a.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              {isBranded && (
                <div className="mb-6 flex justify-center">
                  {renderOrgLogo()}
                </div>
              )}
              <div className="mb-5 flex justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)]">
                  <CheckCircle className="h-7 w-7 text-[var(--green)]" />
                </div>
              </div>
              <h2 className="mb-2 font-display text-[28px] text-[var(--ink)]">
                Check your email
              </h2>
              <p className="mb-4 text-[12px] text-[var(--ink-3)]">
                We sent a secure link to
              </p>
              <p className="mb-4 inline-block rounded-[4px] bg-[var(--bg-subtle)] px-4 py-2 text-[13px] text-[var(--ink)]">
                {submittedEmail}
              </p>
              <p className="text-[11px] text-[var(--ink-3)]">
                The link expires in 1 hour
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
